import { useMemo, useState } from "react";
import { Phone } from "lucide-react";
import { SquareCheckout } from "./SquareCheckout";
import { PASSPORT_PRODUCT_LIST } from "../data/passportProducts";
import { getCountryVisa, buildFeeOptions, feeOptionLabel, SHIPPING_OPTIONS } from "../data/visaOrders";
import { COUNTRY_LIST } from "../lib/countries";
import { PHONE, PHONE_HREF } from "../lib/site";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-[#0a1128] focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20 outline-none transition-[border-color,box-shadow]";
const SHIP_OPTS = [
  { value: "none", label: "No return label needed" },
  { value: "own", label: "Include your own label" },
  { value: "pickup", label: "Pick up from DC office" },
  { value: "fedex_wd", label: "FedEx overnight (weekdays) (+$45.00)" },
  { value: "fedex", label: "FedEx overnight (+$65.00)" },
];
const passportFields = (slug) => [
  { key: "speed", label: "Processing Speed", required: true, options: [
    { value: "emergency", label: "Emergency Service (5–7 business days) — $349" },
    { value: "urgent", label: "Urgent Service (10–12 business days) — $249" },
    { value: "rush", label: "Rush Service (15–20 business days) — $149" },
    { value: "regular", label: "Regular Service (4–6 weeks) — $99" },
  ] },
  ...(["Renew-My-Passport", "Second-Passport", "Passport-Name-Change"].includes(slug)
    ? [{ key: "passport_card", type: "checkbox", label: "Add passport card (+$30)" }] : []),
  { key: "shipping_id", label: "Return Shipping", options: SHIP_OPTS },
];

export const OrderForm = () => {
  const [svc, setSvc] = useState("");
  const [country, setCountry] = useState("");

  const [kind, slug] = svc.includes(":") ? svc.split(":") : [svc, ""];

  const visaFields = useMemo(() => {
    if (kind !== "visa" || !country) return [];
    const v = getCountryVisa(country, COUNTRY_LIST.find((c) => c.slug === country)?.name || country);
    const opts = buildFeeOptions(v);
    return [
      { key: "fee_id", label: "Visa Type & Fees", required: true, options: opts.map((o) => ({ value: o.id, label: feeOptionLabel(o) })) },
      { key: "shipping_id", label: "Return Shipping", options: SHIP_OPTS },
    ];
  }, [kind, country]);

  return (
    <div className="space-y-4" data-testid="order-form">
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(11,25,44,0.10)] border border-black/5 p-6 md:p-7">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">Order a Service</span>
        <h2 className="font-display font-bold text-2xl text-[#0b192c] mt-2 mb-4">Passport & Visa Ordering</h2>
        <label htmlFor="order-service-select" className="block text-sm font-medium text-[#0b192c] mb-1.5">What service do you need? <span className="text-[#b00020]">*<span className="sr-only"> required</span></span></label>
        <select id="order-service-select" aria-required="true" className={inputCls} value={svc} onChange={(e) => { setSvc(e.target.value); setCountry(""); }} data-testid="order-service-select">
          <option value="">Please choose…</option>
          <optgroup label="Passport Services">
            {PASSPORT_PRODUCT_LIST.map((p) => <option key={p.slug} value={`passport:${p.slug}`}>{p.label}</option>)}
          </optgroup>
          <optgroup label="Visa Services">
            <option value="visa">Visa Service (choose country)</option>
          </optgroup>
          <optgroup label="Other">
            <option value="apostille">Apostille / Document Legalization</option>
          </optgroup>
        </select>

        {kind === "visa" && (
          <div className="mt-4">
            <label htmlFor="order-country-select" className="block text-sm font-medium text-[#0b192c] mb-1.5">Destination Country <span className="text-[#b00020]">*<span className="sr-only"> required</span></span></label>
            <select id="order-country-select" aria-required="true" className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)} data-testid="order-country-select">
              <option value="">Please choose…</option>
              {COUNTRY_LIST.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {kind === "passport" && slug && (
        <SquareCheckout title={PASSPORT_PRODUCT_LIST.find((p) => p.slug === slug)?.label || "Passport Service"} category="passport" service={slug} fields={passportFields(slug)} defaults={{ shipping_id: "none" }} />
      )}
      {kind === "visa" && country && (
        <SquareCheckout title={`${COUNTRY_LIST.find((c) => c.slug === country)?.name} Visa`} category="visa" service={country} fields={visaFields} defaults={{ shipping_id: "none" }} />
      )}
      {kind === "apostille" && (
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(11,25,44,0.10)] border border-black/5 p-6 text-center" data-testid="order-apostille">
          <p className="text-slate-600 text-[15px]">Apostille & document legalization is priced per document. Call us for a fast quote.</p>
          <a href={PHONE_HREF} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-3 font-semibold hover:bg-[#ef233c] transition-colors"><Phone className="w-4 h-4" /> Call {PHONE}</a>
        </div>
      )}
    </div>
  );
};
